/** @jsx jsx */

import { ChangeEvent, Fragment, useState } from 'react';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Alert,
  Button,
  Field,
  FormLabel,
  FormLabelRequiredText,
  Modal,
  ModalContent,
  Radio,
  Spacer,
  Text,
  TextInput,
  TooltipWrapper,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Badge from 'app/developer-v3/components/Badge';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB, ROLES } from 'app/developer-v3/constants';
import { useAvroEvent, useIsIntegrationAdmin } from 'app/developer-v3/hooks';

const Styles = {
  buttonWrapper: css`
    display: flex;
    justify-content: flex-end;
    padding: 30px 30px 0px;
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.2);
    margin: 0 -30px;
    box-shadow: 0 30px 0 0 white;
    z-index: 1;
  `,
  labelWrapper: css`
    min-width: 120px;
  `,
  radioLabelWrapper: css`
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 10px;
    text-align: right;
  `,
  textLabelWrapper: css`
    display: flex;
    justify-content: space-between;
    margin-bottom: 2px;
  `,
};

type Props = {
  appTitle: string;
  error?: string;
  initialEmail?: string;
  initialRole?: ROLES;
  initialWelcomeMessage?: string;
  integrationId: string;
  onCloseModal: () => void;
  submitTeamMember: (formData: {
    teamMemberEmail?: string;
    teamMemberRole?: ROLES;
    teamMemberWelcome?: string;
  }) => void;
};

const TeamMemberModal = ({
  appTitle,
  error,
  integrationId,
  initialEmail = '',
  initialRole = undefined,
  initialWelcomeMessage = '',
  onCloseModal,
  submitTeamMember,
}: Props) => {
  // Form fields
  const [email, setEmail] = useState(initialEmail);
  const [role, setRole] = useState<ROLES>(initialRole);
  const [welcomeMessage, setWelcomeMessage] = useState(initialWelcomeMessage);

  // Form Errors
  const [emailError, setEmailError] = useState<string>();
  const [roleError, setRoleError] = useState<string>();
  const [welcomeMessageError, setWelcomeMessageError] = useState<string>();

  const { isAdmin: isIntegrationAdmin } = useIsIntegrationAdmin(
    { integrationId },
    { enabled: true }
  );
  const { emitPartnerUserInteractionEvent } = useAvroEvent();

  /**
   * Validates the form data
   * @returns boolean
   */
  const validateTeamMember = () => {
    const EMAIL_TEST = /(.+)@(.+){2,}/;
    const requiredMessage = 'This is a required field.';

    if (isEmpty(email)) {
      setEmailError(requiredMessage);
    }

    if (!EMAIL_TEST.test(email)) {
      setEmailError(
        `Please include an '@' in the email address. '${email}' is missing an '@'.`
      );
    }

    if (isEmpty(role)) {
      setRoleError(requiredMessage);
    }

    if (isEmpty(welcomeMessage)) {
      setWelcomeMessageError(requiredMessage);
    }

    const hasErrors =
      isEmpty(email) ||
      !EMAIL_TEST.test(email) ||
      isEmpty(role) ||
      isEmpty(welcomeMessage);

    return hasErrors;
  };

  /**
   * Cleans up error state
   */
  const resetErrors = () => {
    setEmailError('');
    setRoleError('');
    setWelcomeMessageError('');
  };

  /** When the role is updated the suggested welcome message is different */
  const onUpdateRole = selectedRole => {
    const welcomePrompt = {
      [ROLES.admin]: `I would like you to help manage ${appTitle}'s Zapier integration and get access to see how it's performing.`,
      [ROLES.collaborator]: `I would like you to help support, share, and promote ${appTitle}'s Zapier integration.`,
      [ROLES.subscriber]: `I would like you to get reports and updates about ${appTitle}'s Zapier integration.`,
    };

    setRole(selectedRole);
    setWelcomeMessage(welcomePrompt[selectedRole]);
    setWelcomeMessageError('');
  };

  const handleSubmit = () => {
    resetErrors();

    if (!validateTeamMember()) {
      submitTeamMember({
        teamMemberEmail: email,
        teamMemberRole: role,
        teamMemberWelcome: welcomeMessage,
      });

      const eventId = uuidv4();
      emitPartnerUserInteractionEvent({
        causationId: eventId,
        eventAction: 'click',
        eventId,
        interactionGoal: 'Add team member to integration',
        interactionName: 'add_team_member',
        interactionRawText: 'Send Invite',
        targetAccessLevel: role,
        targetEmailAddress: email,
      });

      setEmail('');
      setWelcomeMessage('');
    }
  };

  return (
    <Modal onClosed={onCloseModal}>
      <ModalContent>
        <Text
          margin="0px 0px 40px 0px"
          tag="h1"
          textAlign="center"
          type="SectionHeader"
        >
          Invite Team Member
        </Text>
        {error && (
          <Fragment>
            <Alert status="error">{error}</Alert>
            <Spacer height={20} />
          </Fragment>
        )}
        <Field
          error={emailError}
          inputId="team_member_email"
          isRequired={true}
          label="Team Member Email"
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Team Member Email"
              aria-required={true}
              id="team_member_email"
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              value={email}
            />
          )}
        />
        <Spacer height={30} />
        <Field
          error={roleError}
          label="Team Member Role"
          renderInput={() => (
            <Fragment>
              <Spacer height={15} />
              <TooltipWrapper
                content={
                  isIntegrationAdmin
                    ? undefined
                    : 'Only admins can invite other admins'
                }
              >
                {() => (
                  <FormLabel
                    htmlFor="team_member_role_admin"
                    isDisabled={!isIntegrationAdmin}
                  >
                    <Radio
                      aria-label="Admin"
                      checked={role === ROLES.admin}
                      disabled={!isIntegrationAdmin}
                      id="team_member_role_admin"
                      isRequired={true}
                      name="teamMemberRole"
                      onChange={() => onUpdateRole(ROLES.admin)}
                    />
                    <div css={Styles.labelWrapper}>
                      <Text type="Title">Admin</Text>
                    </div>
                    <Spacer height={5} />
                    <Text>
                      Admins will be able to log in and help manage this
                      integration.
                    </Text>
                  </FormLabel>
                )}
              </TooltipWrapper>
              <Spacer height={15} />
              <FormLabel htmlFor="team_member_role_collaborator">
                <Radio
                  aria-label="Collaborator"
                  checked={role === ROLES.collaborator}
                  id="team_member_role_collaborator"
                  isRequired={true}
                  name="teamMemberRole"
                  onChange={() => onUpdateRole(ROLES.collaborator)}
                />
                <div css={Styles.labelWrapper}>
                  <Text type="Title">Collaborator</Text>
                </div>
                <Spacer height={5} />
                <div
                // Load-bearing div. This prevents the badge's
                // `align-self: start` from making the layout look
                // misaligned.
                >
                  <Badge>New</Badge>
                </div>
                <Text>
                  Collaborators can log in to view performance data, user
                  feedback, and access embed tools, but can't make changes.
                </Text>
              </FormLabel>
            </Fragment>
          )}
          renderLabel={labelProps => (
            <FormLabel isRequired={true} {...labelProps}>
              <div css={Styles.radioLabelWrapper}>
                {labelProps.label}
                <Link
                  href={`${BASE_DOC_URL_VB}/manage/invite-team-member`}
                  style={{ textDecoration: 'underline' }}
                  target="_blank"
                >
                  Learn more
                </Link>
                <FormLabelRequiredText>required</FormLabelRequiredText>
              </div>
            </FormLabel>
          )}
        />
        <Spacer height={30} />
        {role && (
          <Fragment>
            <Field
              error={welcomeMessageError}
              isRequired={true}
              label="Add a note to your invitation"
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-label="Welcome message"
                  aria-required={true}
                  isMultiline={true}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setWelcomeMessage(event.target.value);
                  }}
                  rows={4}
                  value={welcomeMessage}
                />
              )}
            />
            <Spacer height={30} />
          </Fragment>
        )}
        <div css={Styles.buttonWrapper}>
          <Button onClick={handleSubmit}>Send Invite</Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default TeamMemberModal;
